import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ApiChangeResponse, ApiManyResponse, ApiResponse, DATE_FORMATS, STATUS } from 'emma-common-ts';
import {
  ApiRequest,
  AudienceAny,
  AudienceCsv,
  AudienceCsvDynamic,
  AudienceReport,
  AUDIENCE_TYPE,
  isAudienceCsv,
  isAudienceCsvDynamic,
  S3Bucket,
  FilterTag,
} from 'emma-common-ts/emma';
import { omit } from 'lodash';
import { from, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { v4 } from 'uuid';

import { EmmaApiService } from './api.service';
import { CurrentAppService } from './current-app.service';
import { S3Service } from './s3.service';

dayjs.extend(utc);

@Injectable({ providedIn: 'root' })
export class AudiencesService {
  constructor(
    private service: EmmaApiService,
    private s3Service: S3Service,
    private currentAppService: CurrentAppService
  ) {}

  getAudiencesReport(params: ApiRequest<never> = {}): Observable<ApiManyResponse<AudienceReport>> {
    return this.service.get('/audiences/report', params);
  }
  getAudience<T = AudienceAny>(id: number): Observable<ApiResponse<T>> {
    return this.service.get(`/audiences/${id}`);
  }
  getAudienceReport(id: number): Observable<ApiResponse<AudienceReport>> {
    return this.service.get(`/audiences/${id}/report`);
  }
  createAudience(audience: AudienceAny, file?: File): Observable<ApiChangeResponse> {
    if ((isAudienceCsv(audience) || isAudienceCsvDynamic(audience)) && !audience.csvFileURL) {
      if (file) {
        return this.createCsvAudience(audience, file);
      } else {
        throw new Error('Audience CSV without file');
      }
    } else {
      return this.service.post(`/audiences`, audience);
    }
  }
  updateAudienceName(id: number, name: string, filterTags: Pick<FilterTag, "name">[]): Observable<ApiChangeResponse> {
    return this.service.put(`/audiences/${id}`, { id, name, filterTags });
  }

  removeAudience(id: number): Observable<ApiChangeResponse> {
    return this.service.delete(`/audiences/${id}`);
  }

  retryAudience(id: number): Observable<ApiChangeResponse> {
    return this.service.post(`/audiences/${id}/retry`);
  }

  activateAudience(id: number): Observable<ApiChangeResponse> {
    return this.service.post(`/audiences/${id}/activate`);
  }

  convertToDynamic(id: number): Observable<ApiChangeResponse> {
    return this.service.post(`/audiences/${id}/convert-to-dynamic`);
  }

  /**
   * Creates a new Audience based on a CSV file stored in a S3 bucket
   * @param audience CSV Audience
   */
  private createCsvAudience(
    audience: AudienceCsv | AudienceCsvDynamic,
    file: File
  ): Observable<ApiChangeResponse> {
    const appId = this.currentAppService.getCurrentApp()?.appConfig.appId;
    if (!appId) {
      throw new Error('Current App appId undefined');
    }
    // Empty file is disallowed
    if (!file.size) {
      return from<[ApiChangeResponse]>([
        {
          status: STATUS.FAILURE,
          data: { id: 0, count: 0, affected: 0, changed: 0 },
          error: 'Empty CSV file',
        },
      ]);
    }
    const prefix = audience.type === AUDIENCE_TYPE.CSV_DYNAMIC ? 'audience_csv_dynamic_' : 'audience_csv_';
    const filename = `${prefix}${appId}_${dayjs().utc().format(DATE_FORMATS.FILE_TIME_FULL)}_${v4()}.csv`;
    return this.s3Service
      .upload({
        Bucket: S3Bucket.AUDIENCES,
        Key: filename,
        Body: file,
        ContentType: 'text/csv',
      })
      .pipe(
        mergeMap((csvFileURL) => {
          if (!csvFileURL) {
            throw new Error('Empty S3 file URL');
          }
          const audienceWithoutFile = omit(audience, ['id', 'csvContent']);
          audienceWithoutFile.csvFileURL = csvFileURL;
          return this.service.post(`/audiences`, audienceWithoutFile);
        })
      );
  }

  downloadCsvAudience(audience: AudienceCsv | AudienceCsvDynamic) {
    const csvFileName = audience.csvFilename;
    const csvS3Name = audience.csvFileURL?.replace('s3://', '');
    if (csvS3Name) {
      return this.s3Service
        .getPublicUrl({
          Bucket: S3Bucket.AUDIENCES,
          Key: csvS3Name,
          ResponseContentDisposition: encodeURIComponent(`attachment; filename="${csvFileName}"`),
        })
        .then((url) => {
          if (url) {
            window.open(url, '_blank');
          }
        });
    }
    return Promise.resolve();
  }

  getAudienceTags(id: number): Observable<ApiResponse<string[]>> {
    return this.service.get(`/audiences/${id}/tags`);
  }
}
